import React, { useEffect, useState } from 'react'
import classNames from 'classnames'
import { isEmpty, isNil } from 'lodash'

import { getRowTypeKey, normalizeFieldKey } from '../helpers'
import { buildRailSegments, RAIL_WIDTH, typeModifier } from '../helpers/connectionRail.helpers'

// Draws each gating condition as a line in the gutter left of the field rows,
// from the field a condition reads to the field it gates.
//
// Row heights aren't knowable up front — a row grows with its conditions, its
// description, and the per-field prompt editor — so anchor positions are
// measured from the DOM and re-measured whenever the list resizes. Rows are
// found by `data-field-key`, which spans both visibility groups, so a
// Processed Later field gated on an agent-visible one still draws.
//
// A line takes the type colour of the field it reads from, matching that
// field's type pill, so the kind of thing doing the gating is visible without
// tracing the line back.
//
// @param {object} props.containerRef the positioned element the rail overlays
// @param {string} [props.focusedKey] field to emphasize the connections of
export const ConnectionRail = ({ containerRef, promptFields, links, focusedKey, revision }) => {
  const [geometry, setGeometry] = useState({ anchors: {}, height: 0 })

  useEffect(() => {
    const container = containerRef.current
    if (!container) {
      return undefined
    }

    const measure = () => {
      const containerTop = container.getBoundingClientRect().top
      const anchors = {}
      container.querySelectorAll('[data-field-key]').forEach((row) => {
        // Anchor on the name line rather than the row box, so the line points
        // at the field's name however tall the row has grown.
        const nameRow = row.querySelector('.rtn-key-name') ?? row
        const box = nameRow.getBoundingClientRect()
        anchors[normalizeFieldKey(row.getAttribute('data-field-key'))] =
          box.top - containerTop + box.height / 2
      })
      setGeometry({ anchors, height: container.getBoundingClientRect().height })
    }

    measure()

    // Rows resize without the window doing so — a description grows, a
    // condition is added, a drag reorders the list.
    const observer = new ResizeObserver(measure)
    observer.observe(container)
    container.querySelectorAll('[data-field-key]').forEach((row) => observer.observe(row))

    window.addEventListener('resize', measure)
    return () => {
      observer.disconnect()
      window.removeEventListener('resize', measure)
    }
    // Deliberately not keyed on `links`: that array is a fresh identity every
    // render, which would re-measure in a loop. A condition changes a row's
    // height, which the observer catches; `revision` catches a reorder or
    // rename, which moves rows without resizing anything.
  }, [containerRef, revision])

  const typeKeysBySource = {}
  promptFields.forEach((field) => {
    typeKeysBySource[normalizeFieldKey(field.key)] = getRowTypeKey(field)
  })

  const segments = buildRailSegments({
    anchors: geometry.anchors,
    links,
    focusedKey,
    typeKeysBySource,
  })
  if (isEmpty(segments)) {
    return null
  }

  return (
    <svg
      className="rtn-rail"
      width={RAIL_WIDTH}
      height={geometry.height}
      aria-hidden="true"
      data-testid="connection-rail"
    >
      {segments.map((segment) => (
        <g
          key={segment.key}
          className={classNames('rtn-rail__edge', {
            'rtn-rail__edge--on': segment.emphasized,
            'rtn-rail__edge--data': segment.isData,
            [`rtn-rail__edge--${typeModifier(segment.typeKey)}`]: !isNil(segment.typeKey),
          })}
          opacity={segment.muted ? 0.15 : 1}
        >
          <path className="rtn-rail__line" d={segment.path} />
          <circle className="rtn-rail__dot" cx={segment.x} cy={segment.sourceY} r="3" />
          <path className="rtn-rail__arrow" d={segment.arrow} />
        </g>
      ))}
    </svg>
  )
}
